import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { acomodarRango, Aplazador, esDia, UltimaRespuesta } from 'src/app/utils/rango-fechas';
import { guardarArchivo } from 'src/app/utils/descargar';

/**
 * CONSULTAS → CUENTAS CT (CCP-03..09; Mario 4-nov, maqueta ~/Documents/reportes-cuentas-ct/preview.html). Lo que entró a las cuentas
 * de CrediTotal en un rango, lo entregado y lo que debe quedar, por banco, cuenta y comercio. TODO lo calcula el núcleo
 * (GET /liquidacion/reportes/cuentas-ct): aquí solo se formatea y se arma el dibujo del gráfico.
 * Carga: cuadros grises (kb-skeleton) con la forma de cada pieza. Si el núcleo manda `preparando {listos,total}` (faltan fotos por día),
 * se muestra lo que haya con el aviso «Preparando N de M días» y se vuelve a consultar cada 15 s SOLO mientras siga preparando.
 */
const DIA_MS = 86400000;
const REPETIR_MS = 15000;
const X0 = 64, X1 = 1228, BASE = 262, ALTO = 224;   // área de dibujo del SVG (viewBox 1240 × 300)
const COLORES: Record<string, string> = { Venezuela: '#2a78d6', Plaza: '#eb6834', Mercantil: '#1baf7a', 'Liquidación': '#eda100', Neteo: '#94a3b8' };
const EXTRA = ['#7c5cd6', '#0ea5b7', '#a16207', '#64748b'];

@Component({
  selector: 'app-liquidacion-cuentas-ct',
  templateUrl: './liquidacion-cuentas-ct.component.html'
})
export class LiquidacionCuentasCtComponent implements OnInit, OnDestroy {
  f = { desde: '', hasta: '', comercio: 'Todos' };
  r: any = null;
  comercios: string[] = [];
  cargando = false;
  error = '';
  ayer = '';
  bancos: { nombre: string; color: string }[] = [];
  diasGrafico: { dia: string; etiqueta: string; celdas: { banco: string; usd: number | null; bs: number | null }[]; total: number }[] = [];
  barras: any[] = [];
  totales: { xc: number; yc: number; etiqueta: string }[] = [];
  ejeY: { y: number; texto: string }[] = [];
  ejeX: { x: number; texto: string }[] = [];
  tip: { x: number; y: number; titulo: string; lineas: string[] } | null = null;
  readonly esqueleto = [0, 1, 2, 3];
  readonly filasGris = [0, 1, 2, 3, 4];

  avisoFecha = '';
  readonly cancelar$ = new Subject<void>();
  private ultima = new UltimaRespuesta(() => this.cancelar$.next());
  private aplazador = new Aplazador();
  private timer: any = null;

  constructor(private liq: LiquidacionService) { }

  ngOnInit(): void {
    this.ayer = this.suma(this.hoyCaracas(), -1);   // hoy no entra: el tope es ayer
    this.f.hasta = this.ayer; this.f.desde = this.suma(this.ayer, -6);
    void this.buscar();
  }
  ngOnDestroy(): void { this.aplazador.cancelar(); this.ultima.cancelarTodo(); clearTimeout(this.timer); }

  // ── fechas (día de Caracas, AAAA-MM-DD) ──
  private hoyCaracas = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date());
  private suma = (dia: string, n: number): string => new Date(Date.parse(`${dia}T00:00:00Z`) + n * DIA_MS).toISOString().slice(0, 10);
  corto = (dia: string): string => (dia ? dia.slice(5).split('-').reverse().join('-') : '');

  /**
   * Filtro nuevo (UTL-22/24): sin las dos fechas completas no se consulta (antes un extremo vacío se leía «un solo día»); si no, se acomoda el rango (hoy no
   * entra), se vuelve al gris y se consulta una sola vez aunque lleguen varios cambios; lo anterior se cancela y su respuesta, si llega tarde, se descarta.
   */
  cambiarFiltro = (cual: 'desde' | 'hasta' | 'comercio' = 'comercio'): void => {
    if (cual !== 'comercio') {
      const g = acomodarRango(this.f, cual, this.ayer);
      this.avisoFecha = g.aviso;
      if (!g.ok) { this.aplazador.cancelar(); return; }
      this.f.desde = g.desde; this.f.hasta = g.hasta;
      this.r = null;
      this.aplazador.en(400, () => { void this.buscar(); });
      return;
    }
    this.aplazador.cancelar(); this.r = null;
    void this.buscar();
  };

  buscar = async (): Promise<void> => {
    if (!esDia(this.f.desde) || !esDia(this.f.hasta)) return;
    const n = this.ultima.nuevo();   // cancela la petición anterior
    clearTimeout(this.timer);
    this.cargando = true; this.error = '';
    try {
      const r = await this.liq.cuentasCt({ desde: this.f.desde, hasta: this.f.hasta, comercio: this.f.comercio }, this.cancelar$);
      if (!this.ultima.vigente(n)) return;   // una respuesta vieja que llega tarde se descarta
      this.r = r;
      if (this.f.comercio === 'Todos' && Array.isArray(r.comparar)) this.comercios = r.comparar.map((c: any) => c.comercio).filter((c: string) => !!c);
      this.armar();
      // solo mientras el núcleo siga preparando días
      if (r.preparando && Number(r.preparando.listos) < Number(r.preparando.total)) this.timer = setTimeout(() => { void this.buscar(); }, REPETIR_MS);
    } catch (e: any) { if (this.ultima.vigente(n)) { this.error = e?.error?.msg || 'No se pudo leer el reporte de cuentas CT.'; this.r = null; } }
    finally { if (this.ultima.vigente(n)) this.cargando = false; }
  };

  // ── descargas (opcionales: nada se genera hasta que se piden) ──
  bajando = { excel: false, pdf: false };
  private nombre = (ext: string): string => `cuentas-ct-${this.f.desde}-a-${this.f.hasta}${this.f.comercio !== 'Todos' ? `-${this.f.comercio}` : ''}.${ext}`;
  private filtros = () => ({ desde: this.f.desde, hasta: this.f.hasta, comercio: this.f.comercio });
  bajarExcel = async (): Promise<void> => {
    this.bajando.excel = true; this.error = '';
    try { guardarArchivo(await this.liq.cuentasCtExcel(this.filtros()), this.nombre('xlsx')); }
    catch (e: any) { this.error = await this.mensaje(e, 'No se pudo bajar el Excel.'); }
    finally { this.bajando.excel = false; }
  };
  bajarPdf = async (): Promise<void> => {
    this.bajando.pdf = true; this.error = '';
    try { guardarArchivo(await this.liq.cuentasCtPdf(this.filtros()), this.nombre('pdf'), 'application/pdf'); }
    catch (e: any) { this.error = await this.mensaje(e, 'No se pudo bajar el PDF.'); }
    finally { this.bajando.pdf = false; }
  };
  /** Con responseType blob el mensaje del núcleo viene dentro de un Blob. */
  private async mensaje(e: any, por_defecto: string): Promise<string> {
    try { return JSON.parse(await e?.error?.text()).msg || por_defecto; } catch (x) { return e?.error?.msg || por_defecto; }
  }

  // ── formato: Bs primero, «—» sin tasa ──
  private num = (v: number, d = 2): string => Number(v).toLocaleString('es', { minimumFractionDigits: d, maximumFractionDigits: d });
  fb = (bs: number | null | undefined): string => (bs === null || bs === undefined ? '—' : `Bs ${this.num(bs)}`);
  fu = (usd: number | null | undefined): string => (usd === null || usd === undefined ? '—' : `USD ${this.num(usd)}`);
  nn = (v: number | null | undefined): string => this.num(Number(v) || 0, 0);
  pc = (v: number | null | undefined): string => `${this.num(Number(v) || 0, 1)} %`;
  millones = (bs: number): string => `${this.num(bs / 1e6, 1)} M`;
  colorDe = (banco: string): string => COLORES[banco] || EXTRA[Math.abs(this.hash(banco)) % EXTRA.length];
  private hash = (s: string): number => Array.from(s).reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7);

  get hayRecibido(): boolean { return (this.r?.resumen?.recibido?.n || 0) > 0 || (this.r?.resumen?.recibido?.usd || 0) > 0; }
  get esUnComercio(): boolean { return this.f.comercio !== 'Todos'; }
  /** El valor de una comprobación: número, o las claves (p. ej. las tasas de comisión distintas) si viene un objeto. */
  valorComprobacion = (c: any): string => (c && typeof c.valor === 'object' && c.valor !== null ? (Object.keys(c.valor).join(' · ') || '—') : this.nn(c?.valor));
  get cierres(): string { return (this.r?.cierres_incluidos || []).map((c: any) => (typeof c === 'string' ? c : (c.dia || c.code || ''))).filter(Boolean).join(', '); }
  get sumaComparar(): boolean { return this.r?.comparar?.length > 0 && !this.esUnComercio; }

  // ── gráfico: barras apiladas por banco, un día por barra ──
  private escala(max: number): { paso: number; tope: number } {
    if (!(max > 0)) return { paso: 1, tope: 1 };
    const e = Math.pow(10, Math.floor(Math.log10(max / 4)));
    const paso = [1, 2, 2.5, 5, 10].map(m => m * e).find(p => max / p <= 4) || 10 * e;
    return { paso, tope: Math.ceil(max / paso) * paso };
  }
  private armar(): void {
    const filas: any[] = this.r?.por_banco_dia || [];
    const nombres: string[] = (this.r?.por_banco || []).map((b: any) => b.banco);
    for (const x of filas) if (!nombres.includes(x.banco)) nombres.push(x.banco);
    this.bancos = nombres.map(nombre => ({ nombre, color: this.colorDe(nombre) }));
    const dias: string[] = [...new Set<string>(filas.map(x => x.dia))].sort();
    const por = new Map<string, any>(filas.map(x => [`${x.dia}|${x.banco}`, x] as [string, any]));
    this.diasGrafico = dias.map(dia => {
      const celdas = nombres.map(banco => { const x = por.get(`${dia}|${banco}`); return { banco, usd: x ? x.usd : null, bs: x ? x.bs : null }; });
      // total del día = lo que el gráfico apila (suma de los Bs de sus bancos, en centavos)
      return { dia, etiqueta: this.corto(dia), celdas, total: celdas.reduce((a, c) => a + Math.round((c.bs || 0) * 100), 0) / 100 };
    });
    const max = Math.max(0, ...this.diasGrafico.map(d => d.total));
    const { paso, tope } = this.escala(max); const k = ALTO / tope;
    const n = this.diasGrafico.length || 1, slot = (X1 - X0) / n, bw = Math.min(90, Math.max(6, slot * 0.62));
    this.barras = []; this.totales = [];
    this.diasGrafico.forEach((d, i) => {
      const x = X0 + i * slot + (slot - bw) / 2; let y0 = BASE;
      d.celdas.forEach(c => {
        if (!(c.bs && c.bs > 0)) return;
        const h = Math.max(1, c.bs * k), y = y0 - h;
        this.barras.push({
          x, y: y + 1, w: bw, h: Math.max(h - 2, 1), color: this.colorDe(c.banco), banco: c.banco, dia: d.dia,
          titulo: `${d.etiqueta} · ${c.banco} · ${this.fb(c.bs)} · ${this.fu(c.usd)}`, lineas: [this.fb(c.bs), this.fu(c.usd)], etiqueta: h > 22 && bw > 58 ? this.millones(c.bs) : '', xc: x + bw / 2, yc: y + h / 2 + 4,
        });
        y0 = y;
      });
      this.totales.push({ xc: x + bw / 2, yc: y0 - 6, etiqueta: this.millones(d.total) });
    });
    this.ejeY = [];
    for (let v = 0; v <= tope + paso / 1000; v += paso) this.ejeY.push({ y: BASE - v * k, texto: this.millones(v) });
    const cada = Math.max(1, Math.ceil(n / 12));
    this.ejeX = this.diasGrafico.flatMap((d, i) => (i % cada === 0 ? [{ x: X0 + (i + 0.5) * slot, texto: d.etiqueta }] : []));
  }

  /** Ancho (0-100) de la barra de una partida respecto a la mayor; solo dibujo. */
  ancho = (bs: number): number => { const m = Math.max(0, ...(this.r?.composicion?.partidas || []).map((p: any) => Math.abs(p.bs))); return m > 0 ? Math.max(0.8, Math.abs(bs) / m * 100) : 0; };
  hayDiferencia = (): boolean => Math.abs(this.r?.composicion?.diferencia?.bs || 0) >= 0.005 || Math.abs(this.r?.composicion?.diferencia?.usd || 0) >= 0.005;

  verTip = (e: MouseEvent, b: any): void => { this.tip = { x: Math.min(e.clientX + 12, window.innerWidth - 200), y: e.clientY + 14, titulo: `${this.corto(b.dia)} · ${b.banco}`, lineas: b.lineas }; };
  quitarTip = (): void => { this.tip = null; };
}
